import {Autocomplete, Drawer, Input, TextInput, Textarea} from '@mantine/core';
import {useMediaQuery} from '../../../../../../shared/services/useMediaQuery';
import {
  COMMON_DRAWER_STYLES,
  getDrawerSize,
} from '../../../../constants/drawerStyles';
import {Z_INDEX} from '../../../../constants/zIndex';
import {ClickToEditDateInputContainer} from '../../ClickToEditDateInputContainer';
import {ModalCancelButton} from '../ModalCancelButton';
import {ModalSubmitButton} from '../ModalSubmitButton';

export type AddMgtFormDataType = {
  companyId: number;
  companyName: string;
  stepOrder: number;
  stepName: string;
  deadline?: string;
  notes: string;
  result?: string;
  startDate?: string;
  endDate?: string;
  place?: string;
};

type Props = {
  opened: boolean;
  onClose: () => void;
  onSubmit: () => void;
  formData: AddMgtFormDataType;
  errors?: Partial<Record<keyof AddMgtFormDataType, string>>;
  onFormChange: (
    field: keyof AddMgtFormDataType,
    value: string | number | undefined,
  ) => void;
  onFieldBlur?: (field: keyof AddMgtFormDataType) => void;
  isLoading?: boolean;
  companyList: {id: number; value: string}[];
  onCompanyNameBlur?: () => void;
  errorMessage?: string | null;
  onBack?: () => void;
};

export const ProgressMgtCreateDrawer: React.FC<Props> = ({
  opened,
  onClose,
  onSubmit,
  formData,
  errors,
  onFormChange,
  onFieldBlur,
  isLoading = false,
  companyList,
  onCompanyNameBlur,
  onBack,
}) => {
  const {isPC} = useMediaQuery();

  const hasValidationErrors = errors && Object.keys(errors).length > 0;
  const isFormValid =
    formData.companyName.trim() &&
    formData.stepName.trim() &&
    !hasValidationErrors;

  const handleFieldChange = (
    field: keyof AddMgtFormDataType,
    value: string | number | undefined,
  ) => {
    onFormChange(field, value);
  };

  const handleSubmit = () => {
    onSubmit();
  };

  const handleBack = () => {
    // onBackがある場合は前のドロワーに戻り、ない場合はドロワーを閉じる
    if (onBack) {
      onBack();
    } else {
      onClose();
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title="新規企業の選考予定を追加"
      position="right"
      size={getDrawerSize(!isPC)}
      zIndex={Z_INDEX.FORM_DRAWER}
      styles={COMMON_DRAWER_STYLES}
    >
      <div className="flex flex-col gap-24">
        <div className="flex flex-col gap-16">
          <Autocomplete
            id="companyName"
            label="企業名"
            value={formData.companyName}
            onChange={(value) => handleFieldChange('companyName', value)}
            onBlur={() => onCompanyNameBlur?.()}
            placeholder="企業名を入力または選択してください"
            withAsterisk
            error={errors?.companyName}
            data={companyList}
            maxDropdownHeight={320}
            radius={24}
          />
          <TextInput
            id="stepName"
            label="選考予定"
            type="text"
            value={formData.stepName}
            onChange={(e) => handleFieldChange('stepName', e.target.value)}
            onBlur={() => onFieldBlur?.('stepName')}
            placeholder="例：一次面接"
            withAsterisk
            error={errors?.stepName}
            radius={24}
          />
          <div className="flex flex-col gap-4">
            <Input.Label>締切</Input.Label>
            <ClickToEditDateInputContainer
              value={formData.deadline}
              onSave={(value) => handleFieldChange('deadline', value)}
              error={errors?.deadline}
              withTime
            />
          </div>
          {/* 日程: 開始日時と終了日時を横並びで表示 */}
          <div className="flex flex-col gap-4">
            <Input.Label>日程</Input.Label>
            <div className="flex items-center gap-8">
              <ClickToEditDateInputContainer
                value={formData.startDate}
                onSave={(value) => handleFieldChange('startDate', value)}
                error={errors?.startDate}
                withTime
                displayClassName="pc:flex-none pc:w-[200px]"
              />
              <span className="text-sm text-black-3">〜</span>
              <ClickToEditDateInputContainer
                value={formData.endDate}
                onSave={(value) => handleFieldChange('endDate', value)}
                error={errors?.endDate}
                withTime
                displayClassName="pc:flex-none pc:w-[200px]"
              />
            </div>
          </div>
          <TextInput
            id="place"
            label="場所"
            type="text"
            value={formData.place || ''}
            onChange={(e) => handleFieldChange('place', e.target.value)}
            onBlur={() => onFieldBlur?.('place')}
            placeholder="例：オンライン、東京オフィス"
            error={errors?.place}
            radius={24}
          />
          <Textarea
            id="notes"
            label="メモ"
            value={formData.notes}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
            onBlur={() => onFieldBlur?.('notes')}
            placeholder="例：持ち物、服装、注意事項"
            error={errors?.notes}
          />
        </div>
        <div className="flex items-center justify-center gap-16">
          <ModalCancelButton onClick={handleBack}>キャンセル</ModalCancelButton>
          <ModalSubmitButton
            onClick={handleSubmit}
            disabled={!isFormValid}
            isLoading={isLoading}
          >
            保存
          </ModalSubmitButton>
        </div>
      </div>
    </Drawer>
  );
};
